package nl.volleyplanner.web.controller

import kotlinx.browser.document
import kotlinx.html.*
import kotlinx.html.dom.create
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.web.base.StaticPageController
import org.w3c.dom.HTMLElement

class ProductDetailsController(override val parent: VolleyplannerWebPageIndexController) : StaticPageController() {
    override val name = "ProductDetailsController"

    override val path = "/product-details"
    override val requiredAuthLevel = AuthLevel.AnonymousPublic
    override val title = "Volleyplanner Details"
    override val childPages = emptyList<nl.volleyplanner.web.base.WebPageController>()

    override fun createRuntimeInstance() = ProductDetailsController(parent)

    fun FlowContent.twoColHero(reverse: Boolean, blockA: DIV.() -> Unit, blockB: DIV.() -> Unit, borderBottom: Boolean = true) {
        div (classes = "container py-5") {
            if (borderBottom) { classes += "border-bottom" }
            div(classes = "row align-items-center g-5") {
                if (reverse) {
                    classes += "flex-lg-row-reverse"
                }
                div(classes = "col-10 col-sm-8 col-lg-6") {
                    blockA()
                }
                div(classes = "col-lg-6") {
                    blockB()
                }
            }
        }
    }

    override suspend fun renderContent(): HTMLElement {
        return document.create.div {
            twoColHero(true, {
                div (classes = "border rounded-3 shadow-lg mw-100 h-auto p-3") {
                    h5 (classes="fw-bold") {+"Standaard mogelijkheden"}
                    ul (classes = "web-checklist-ok") {
                        li { +"Alleen inplannen als het lid zelf een thuiswedstrijd heeft" }
                        li { +"Leden niet inplannen tijdens hun eigen wedstrijd" }
                        li { +"Leden maximaal 1x per speelweek inplannen" }
                        li { +"Tijd tussen taak en eigen wedstrijd minimaliseren" }
                        li { +"Taken eerlijk verdelen over alle leden" }
                        li { +"Configureer wie welk niveau/team mag fluiten/tellen" }
                        li { +"Optioneel: vrijwilligers minder vaak of niet inplannen" }
                        li { +"Optioneel: configureer limieten voor de jeugd" }
                        li { +"Inzicht in de kwaliteit en pijnpunten van het schema" }
                        li { +"Indien gewenst zijn handmatige aanpassingen altijd mogelijk" }
                    }
                }
            }, {
                h1 (classes = "display-5 fw-bold text-body-emphasis lh-1 mb-3") {
                    +"Takenplanner waar je iets aan hebt"
                }
                p (classes = "lead") {
                    +"Voor volleyballers door volleyballers. We snappen als geen ander wat voor puzzel het elke keer weer is om het fluit- en telschema rond te krijgen. "
                }
                p (classes = "lead") {
                    +"Met succes gebruikt bij een vereniging met 42 teams met senioren en jeugd op meerdere niveaus, waarbij het eenvoudig is om het beleid en configuratie per niveau aan te passen."
                }
            })
            twoColHero(false, {
                img(
                    src = "img/web/import-leden.png",
                    classes = "img-fluid border rounded-3 shadow-lg",
                    alt = "Screenshot met spreadsheet om leden in te voeren"
                ) {}
                p (classes = "fw-small text-body-secondary") { em { +"Voor afbeeldingen zijn fictieve namen gebruikt" } }
            }, {
                h1(classes = "display-5 fw-bold text-body-emphasis lh-1 mb-3") {
                    +"Geef eenvoudig jullie leden door"
                }
                p(classes = "lead") {
                    +"De enige data die je zelf aan moet leveren in één simpele spreadsheet. Aan een naam, team(s) en scheidsrechtersgroep hebben we genoeg voor een planning! "
                    +"Je kan leden in meerdere teams zetten voor bv regelmatige invallers als jeugd zodat ze bij wedstrijden voor beide teams niet worden ingepland."
                }
            })
            twoColHero(true, {
                img(
                    src = "img/web/import-vrijwilligers.png",
                    classes = "img-fluid border rounded-3 shadow-lg",
                    alt = "Screenshot met spreadsheet om vrijwilligers in te voeren"
                ) {}
                p (classes = "fw-small text-body-secondary") { em { +"Voor afbeeldingen zijn fictieve namen gebruikt" } }
            }, {
                h1(classes = "display-5 fw-bold text-body-emphasis lh-1 mb-3") {
                    +"Optioneel: Hou rekening met vrijwilligers"
                }
                p(classes = "lead") {
                    +"Naast leden is het ook mogelijk om jullie vrijwilligers door te geven. Deze mensen worden gedeeltelijk of geheel ontzien in de planning. "
                }
            })

            // Centered container: img bottom
            div (classes = "container text-center border-bottom py-5") {
                div {
                    h1(classes = "display-5 fw-bold text-body-emphasis") {
                        +"Automatisch up-to-date met Nevobo"
                    }
                    div(classes = "col-lg-6 mx-auto") {
                        p(classes = "lead") { +"Wedstrijdgegevens worden automatisch opgehaald bij de Nevobo. Ook last-minute wijzigingen kunnen nog worden verwerkt als je al bent begonnen met plannen." }
                    }
                }
                div {
                    img(
                        src = "img/web/nevobo-sync.png",
                        classes = "img-fluid border rounded-3 shadow-lg",
                        alt = "Screenshot met nevobo competitie en volleyplanner"
                    ) {
                        style = "max-height: 400px;"
                    }
                }
            }

            twoColHero(true,
                {
                    img(
                        src = "img/web/config-beleid.png",
                        classes = "img-fluid border rounded-3 shadow-lg",
                        alt = "Screenshot voor configuratie voor wie wat mag fluiten en tellen per niveau"
                    ) {}
                },
                {
                    h1(classes = "display-5 fw-bold text-body-emphasis lh-1 mb-3") {
                        +"Flexibele configuratie per niveau"
                    }
                    p(classes = "lead") {
                        +"Bepaal zelf jullie beleid per niveau. Welke groepen scheidsrechters deel je waar in en wie wil je waar laten tellen? "
                    }
                }
            )
            twoColHero(false,
                {
                    img(
                        src = "img/web/config-jeugd.png",
                        classes = "img-fluid border rounded-3 shadow-lg",
                        alt = "Screenshot voor specifieke configuratie voor de jeugd"
                    ) {}
                },
                {
                    h1(classes = "display-5 fw-bold text-body-emphasis lh-1 mb-3") {
                        +"Stel limieten in voor de jeugd"
                    }
                    p(classes = "lead") {
                        +"Per leeftijdsgroep is het mogelijk om limieten in te stellen. Bijvoorbeeld om de jongere jeugd niet 's avonds laat pas naar huis te laten gaan."
                    }
                }
            )

            twoColHero(true,
                {
                    img(
                        src = "img/web/programma-statistieken.png",
                        classes = "img-fluid border rounded-3 shadow-lg",
                        alt = "Screenshot met thuisprogramma statistieken voorbeeld"
                    ) {}
                    p (classes = "fw-small text-body-secondary") { em { +"Voor afbeeldingen zijn fictieve namen gebruikt" } }
                },
                {
                    h1(classes = "display-5 fw-bold text-body-emphasis lh-1 mb-3") {
                        +"Inzicht in planningskwaliteit"
                    }
                    p(classes = "lead") {
                        +"Krijg direct inzicht in hoe optimaal de planning is door te zien welke indelingen goed en slecht scoren. "
                        +"Bij slechte scores wordt uitleg gegeven over de reden. Zo krijg je ook direct inzicht in de pijnpunten in jullie schema. "
                    }
                    p(classes = "lead") {
                        +"Je kan altijd zelf handmatige aanpassingen maken of de planner opnieuw met een andere configuratie/beleid draaien. "
                    }
                }
            )
            twoColHero(false,
                {
                    img(
                        src = "img/web/thuisprogramma.png",
                        classes = "img-fluid border rounded-3 shadow-lg",
                        alt = "Screenshot met thuisprogramma voorbeeld"
                    ) {}
                    p (classes = "fw-small text-body-secondary") { em { +"Voor afbeeldingen zijn fictieve namen gebruikt" } }
                },
                {
                    h1(classes = "display-5 fw-bold text-body-emphasis lh-1 mb-3") {
                        +"Schema delen met leden"
                    }
                    p(classes = "lead") {
                        unsafe { +"Het schema kan worden gedownload als PDF om verder te gebruiken. Ook is het mogelijk om een unieke link* naar het schema te delen waarbij leden het schema met elk apparaat kunnen raadplegen. " }
                    }
                    p (classes = "lead") {
                        +"Het uiteindelijke schema bevat van leden enkel hun voornaam + team."
                    }
                    p (classes = "fw-small text-body-secondary") {
                        +"* Waar de pilot in principe vrijblijvend is, zal voor de unieke link wel een vergoeding noodzakelijk zijn omdat dit gedurende het seizoen ook serverruimte zal vragen."
                    }
                }
            )

            twoColHero(true,
                {
                    div (classes = "text-center") {
                        a(classes = "btn btn-primary btn-lg") {
                            role = "button"
                            href = "/registreren"
                            +"Aan de slag!"
                        }
                    }
                }, {
                    h1(classes = "display-5 fw-bold text-body-emphasis lh-1 mb-3") {
                        +"Vrijblijvende pilot"
                    }
                    p (classes = "lead") {
                        +"Als hobbyproject ontstaan bij mijn eigen vereniging ben ik benieuwd of dit ook goed werkt bij andere geinteresseerde verenigingen. "
                        +"Voor de tweede competitiehelft hoop ik dit met een paar verenigingen te kunnen testen om het bij success vanaf volgend seizoen verder uit te rollen."
                    }
                    p (classes = "lead") {
                        +"De applicatie is momenteel in de laatste fase van ontwikkeling. En zal voor de (definitieve) publicatie tweede helft jeugd nog worden gelanceerd."
                    }
                }
            )
        }
    }
}